// Single source of truth for the primary navigation.
//
// Sidebar reads from STEPS so adding/renaming/reordering a nav item is
// a one-line change. Step status (pending/active/complete/warning) is
// derived at runtime from the current URL (Phase 2); later phases will
// read actual brand-setup / campaign / render state.
//
// Reorg note: Upload + Detect were retired from the primary nav and
// fold into the Generate Ads wizard (campaign → products → settings →
// generate). The deep-link routes (/upload, /detect, /media-library,
// /catalog) still resolve so internal links keep working.

export type StepKey = 'integrations' | 'image-studio' | 'campaigns' | 'product-ads' | 'ugc-ads';
export type StepStatus = 'pending' | 'active' | 'complete' | 'warning';

export type StepDef = {
  key:         StepKey;
  path:        string;
  label:       string;
  description: string;
};

// Primary numbered flow — Integrations (1) → Campaigns (2) → Product Ads (3).
// Home sits ABOVE this list in the sidebar as an unnumbered entry — it's
// the agent, not a pipeline step, and the operator lands there by default
// after login.
export const STEPS: readonly StepDef[] = [
  { key: 'integrations', path: '/integrations', label: 'Integrations',    description: 'Connect Meta, Google, IG, Shopify' },
  { key: 'image-studio', path: '/image-studio', label: 'Image Studio',    description: 'Chat-driven lifestyle imagery with producer-picked seeds' },
  { key: 'campaigns',    path: '/campaigns',    label: 'Campaigns',       description: 'Sync & generate' },
  { key: 'product-ads',  path: '/product-ads',  label: 'Product Ads',  description: 'Generate & manage at scale' },
  { key: 'ugc-ads',      path: '/ugc-ads',      label: 'UGC Ads',      description: 'Attach social posts to targets' }
] as const;

// Home is a first-class nav destination but not part of the numbered
// pipeline flow. Rendered above STEPS in the sidebar with the "chat"
// icon instead of a number.
export const HOME_NAV = { path: '/home', label: 'Home', description: 'Ask the agent' } as const;

// Studio Zara sits beside Home. It is not a numbered pipeline step and it
// is not part of SECONDARY_NAV. The shell renders it only when the flag
// or the preview switch is on.
export const ZARA_NAV = {
  path: '/zara',
  label: 'Studio Zara',
  description: 'Chat with Zara'
} as const;

// Secondary destinations — utility pages that aren't part of the
// primary nav but the operator reaches often. Rendered below the
// primary STEPS in the sidebar with simpler styling.
export type SecondaryNavItem = {
  path:       string;
  label:      string;
  // Hidden from users without User.isSuperAdmin. Route still resolves
  // via deep-link if a non-admin knows the URL — the backend gates
  // access on its own (e.g. Sales Demos requires membership to the
  // sales-demos advertiser, which super-admins auto-get).
  adminOnly?: boolean;
};

export const SECONDARY_NAV: readonly SecondaryNavItem[] = [
  { path: '/catalog',          label: 'Product Catalog' },
  { path: '/detect',           label: 'Detect Review' },
  { path: '/media-library',    label: 'Media Library' },
  { path: '/render-activity',  label: 'Render Activity' },
  { path: '/team',             label: 'Team' },
  { path: '/settings',         label: 'Settings' },
  { path: '/brand',            label: 'Brand' },
  { path: '/ad-showcase',      label: 'Ad Showcase' },
  { path: '/sales-demos',      label: 'Sales Demos', adminOnly: true },
  { path: '/admin/shopify-requests', label: 'Shopify Requests', adminOnly: true },
  { path: '/admin/shopify-health',   label: 'Shopify Health',   adminOnly: true },
  { path: '/admin/system-config',    label: 'System Config',    adminOnly: true },
  { path: '/admin/settings',   label: 'Admin',       adminOnly: true }
] as const;

// Sidebar hides adminOnly items with this check. Connections nav uses
// the same rule for Shopify health.
export function viewerIsSuperAdmin(auth: {
  status: string;
  user?: { isSuperAdmin?: boolean };
}): boolean {
  return auth.status === 'authenticated' && !!auth.user && auth.user.isSuperAdmin === true;
}

// Out-of-band routes that should still highlight one of the primary
// nav items when the user lands on them (deep links + the wizard).
const ALIAS_TO_STEP: Record<string, StepKey> = {
  '/generate-ads':  'campaigns',
  '/upload':        'campaigns',
  // /ads is the secondary post-generate run-status view — keep the
  // sidebar highlighting "Product Ads" so operators don't lose context
  // when the wizard redirects them.
  '/ads':           'product-ads',
  // The UGC-ads wizard is the "generate flow" for the UGC Ads surface;
  // keep the sidebar anchored on UGC Ads when operators are inside it.
  '/ugc-wizard':    'ugc-ads'
  // /detect is now a top-level secondary nav entry (Detect Review),
  // no longer aliased to a primary step.
};

export function statusFromPath(currentPath: string): Record<StepKey, StepStatus> {
  let active: StepKey | undefined = STEPS.find(s => currentPath.startsWith(s.path))?.key;
  if (!active) {
    for (const [alias, step] of Object.entries(ALIAS_TO_STEP)) {
      if (currentPath.startsWith(alias)) { active = step; break; }
    }
  }
  const out = {} as Record<StepKey, StepStatus>;
  for (const s of STEPS) out[s.key] = s.key === active ? 'active' : 'pending';
  return out;
}
